import { FieldValues, Control, Path, Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { FormControl, TextFieldProps } from '@mui/material'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker, DatePickerProps } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import dayjs, { Dayjs } from 'dayjs'
import { getLang } from 'translation/translation-helper'

interface ControlledMuiDateFieldProps<T extends FieldValues>
    extends Omit<DatePickerProps<Dayjs>, 'name' | 'error' | 'renderInput' | 'onChange' | 'value'> {
    control: Control<T>
    name: Path<T>
    label?: string
    fullWidth?: boolean
    inputProps?: TextFieldProps
    onChangeCb?: (date: dayjs.Dayjs | null) => void
}

export const ControlledMuiDateField = <T extends FieldValues>({
    control,
    name,
    label,
    fullWidth,
    inputProps,
    onChangeCb,
    slotProps,
    ...rest
}: ControlledMuiDateFieldProps<T>) => {
    const { t } = useTranslation()
    const lang = getLang()

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={lang}>
            <FormControl fullWidth={fullWidth}>
                <Controller
                    name={name}
                    control={control}
                    render={({ field: { ref, onBlur, onChange, name, value, disabled }, fieldState }) => (
                        <DatePicker
                            {...rest}
                            inputRef={ref}
                            label={label}
                            disabled={typeof rest.disabled === 'boolean' ? rest.disabled : disabled}
                            value={value ? dayjs(value) : null}
                            onChange={(date: dayjs.Dayjs | null) => {
                                onChange(date?.toDate() || '')
                                onChangeCb?.(date)
                            }}
                            slotProps={{
                                ...slotProps,
                                textField: {
                                    ...inputProps,
                                    onBlur,
                                    name,
                                    error: !!fieldState.error,
                                    helperText: fieldState.error?.message ? t(fieldState.error.message) : undefined,
                                },
                            }}
                        />
                    )}
                />
            </FormControl>
        </LocalizationProvider>
    )
}
